<template>
    <div v-if="!hasLoadedAllAccesspoints" class="d-flex justify-center pt-8">
    <v-progress-circular indeterminate :size="106" :width="8" color="primary" :aria-label="$t('common.loading')"></v-progress-circular>
  </div>

  <div v-else>
    <v-toolbar elevation="0" class="user-permissions-toolbar mb-1 px-2" rounded>
      <!-- Left Side Button -->
      <div>
        <v-btn
            :size="$vuetify.display.smAndDown ? 'small' : undefined"
            @click="goBack"
            class="bg-secondaryButtons"
            elevation="0"
            block
        >
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </div>

      <!-- Optional Node Selection Button -->
      <div v-if="!addNewNodeId" class="ml-2">
        <SelectNodeDialog
            v-model="addNewNodeId"
            :disabledNodeIds="accessPointIds"
            :isDisabledSelectingNode="isDisabledSelectingNode"
            :userType="getUserTenants?.[0]?.userType"
        >
          <v-btn elevation="0" class="bg-primary">
            {{ $t('routes.users.addAccessPoint') }}
          </v-btn>
        </SelectNodeDialog>
      </div>

      <!-- Spacer to Push Content to the Right -->
      <v-spacer></v-spacer>

      <!-- User Name Alert on the Right -->
      <div v-if="!getEditUser" class="font-italic bg-error">
        {{ $t('menus.createUser.userNotSelected') }}
      </div>
      <div v-else class="font-italic pa-1" style="word-break: break-all" data-cy="dialog-setPermissions-userDetails">
        {{ getEditUser.firstname }} {{ getEditUser.lastname }} ( {{ getEditUser.email }} )
      </div>
    </v-toolbar>

    <v-card elevation="0" class="mt-5 vb-user-accesspoints">
      <v-card-text v-if="getEditUser">
        <h2 class="mb-2">{{ $t('routes.users.userAccessPoints') }}</h2>

        <v-list v-if="getUserTenants" class="userAccesspoints">
          <!-- List Item with Larger Text and Spacing -->
          <v-list-item v-for="(accessPointId, index) in accessPointIds" :key="accessPointId">
            <template #default>
              <SelectNodeDialog
                  v-if="canControlNodeUsers(accessPointId)"
                  :modelValue="accessPointIds[index]"
                  @update:modelValue="(value) => updateAccessPointId(index, value)"
                  v-slot:default="slotProps"
                  :isDisabledSelectingNode="isDisabledSelectingNode"
                  :disabledNodeIds="getDisabledNodeIds(accessPointId)"
                  :userType="getUserTenants[0]?.userType"
              >
                <h4 v-if="slotProps && slotProps.selectedNode" style="cursor: pointer;">
                  {{ slotProps.selectedNode.name }}
                </h4>
              </SelectNodeDialog>
              <h4 v-else>
                {{ getUserTenants.flatMap(tenant => tenant.accessPoints).find((ap) => ap.node.id === accessPointId)?.node.name }}
              </h4>
            </template>

            <template #append>
              <div v-if="canControlNodeUsers(accessPointId)" class="d-flex">
                <v-btn
                  v-if="hasMoreThanOneAccessPoints"
                  class=""
                  @click="removeFolder(accessPointId)"
                  color="secondaryButtons"
                  elevation="0"
                >
                  <v-icon>mdi-trash-can</v-icon>
                  <span v-show="$vuetify.display.mdAndUp">
                    {{ $t('common.delete') }}
                  </span>
                </v-btn>

                <UserPermissionsDialog
                  v-if="getEditUser"
                  :getEditUser="getEditUser"
                  :accessPointId="accessPointId"
                >
                  <v-btn  color="secondaryButtons" elevation="0" class="ml-2">
                    <v-icon>mdi-key</v-icon>
                    <span v-show="$vuetify.display.mdAndUp">
                      {{ $t('common.permissions') }}
                    </span>
                  </v-btn>
                </UserPermissionsDialog>
              </div>
            </template>
          </v-list-item>

          <!-- Divider Between Items -->
          <!-- <v-divider  v-if="index < accessPointIds.length - 1"></v-divider> -->
        </v-list>
      </v-card-text>
    </v-card>

    <AccesspointAddDialog
    v-if="getEditUser"
    v-model="showingAccesspointAddDialog"
    :user="getEditUser"
    :nodeId="addNewNodeId"
    :userType="getUserTenants?.[0]?.userType"
    @dialogClosed="accesspointAddClosed()"
    >
    </AccesspointAddDialog>

    <AccesspointUpdateDialog
    v-if="getEditUser"
    v-model="showingAccesspointUpdateDialog"
    :user="getEditUser"
    :oldNodeId="accesspointUpdateOldId"
    :newNodeId="accesspointUpdateNewId"
    :userType="getUserTenants?.[0]?.userType"
    @dialogClosed="accesspointUpdatedClosed()"
    >
    </AccesspointUpdateDialog>

    <AccesspointDeleteDialog
    v-if="getEditUser"
    v-model="showingAccesspointDeleteDialog"
    :user="getEditUser"
    :nodeId="deleteNodeId"
    @dialogClosed="accesspointDeleteClosed()"
    >
    </AccesspointDeleteDialog>

  </div>
</template>


<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useUserStore } from '@/stores/UserStore.js'
import { useNodeStore } from '@/stores/NodeStore.js';
import SelectNodeDialog from './components/dialogs/SelectNodeDialog.vue'
import AccesspointAddDialog from './components/dialogs/AccesspointAddDialog.vue';
import AccesspointUpdateDialog from './components/dialogs/AccesspointUpdateDialog.vue';
import AccesspointDeleteDialog from './components/dialogs/AccesspointDeleteDialog.vue';
import UserPermissionsDialog from './components/dialogs/UserPermissionsDialog.vue';

export default {
  name: "UserPermissions",
  components: {
    SelectNodeDialog,
    AccesspointAddDialog,
    AccesspointUpdateDialog,
    AccesspointDeleteDialog,
    UserPermissionsDialog,
  },
  data() {
    return {
      accessPointIds: [],
      isDisabledSelectingNode: false,

      showingAccesspointAddDialog: false,
      showingAccesspointUpdateDialog: false,
      showingAccesspointDeleteDialog: false,
      ignoreAccessPointIdsUpdate: false,

      addNewNodeId: '',
      accesspointUpdateOldId: '',
      accesspointUpdateNewId: '',
      deleteNodeId: '',

      selectedAccessPointId: '',
    };
  },
  mounted() {
    // Using the editUser from the state manager
    // We don't take the userId from the url so that we can avoud broken links.
    if (!this.getEditUser) {
      this.goBack();
      return;
    }

    this.checkUser(this.getEditUser.id, this.activeAccessPointId)
      .then((response) => {
        if (!response.success) {
          this.accessPointIds = [];
        } else {
          this.refreshAccessPoints();
        }
      })
      .catch(error => {
        console.log(error);

        this.accessPointIds = [];
      });
  },
  watch: {
    addNewNodeId(newValue) {
      if (newValue) {
        // TODO: send add-new-accesspoint api request
        this.showingAccesspointAddDialog = true;

        // Reset the SelectNodeDialog by hiding it and the showing it again

      }
    },
    getEditUser() {
      this.refreshAccessPoints();
    },
    accessPointIds(newArray) {
      if (newArray.length === 0) {
        this.$showMessage(this.$t('routes.users.accessPointOrUserUpdated'), { type: 'warning' });
        this.goBack();
        return;
      }

      if (this.ignoreAccessPointIdsUpdate) {
        this.ignoreAccessPointIdsUpdate = false;
        return;
      }

      // show the dialog with "Confirm AP update message"
      const oldAccesspointIds = [];
      if (this.getUserTenants) {
        for (const tenant of this.getUserTenants) {
          oldAccesspointIds.push(...tenant.accessPoints.map(x => x.node.id));
        }
      }

      const { newId, oldId } = this.findUniqueIds(oldAccesspointIds, newArray);

      if (oldAccesspointIds.includes(this.selectedAccessPointId)) {
        this.accesspointUpdateNewId = this.selectedAccessPointId;
        this.accesspointUpdateOldId = this.selectedAccessPointId;
      } else {
        this.accesspointUpdateOldId = oldId;
        this.accesspointUpdateNewId = newId;
      }

      this.showingAccesspointUpdateDialog = true;
    },

    hasLoadedAllAccesspoints(newValue) {
      if (!newValue) {
        const nodeStore = useNodeStore();
        // for each missing node - fetch it from the backend
        let promises = [];
        for (const id of this.accessPointIds) {
          const node = nodeStore.getNodeById(id);
          if (!node) {
            promises.push(nodeStore.getNodeInfo(id));
          }
        }

        Promise.all(promises);
      }
    }
  },
  methods: {
    goBack() {
      this.$router.push('/console/users');
    },
    findUniqueIds(oldArray, newArray) {
      const oldSet = new Set(oldArray);
      const newSet = new Set(newArray);

      const newId = newArray.find(id => !oldSet.has(id));
      const oldId = oldArray.find(id => !newSet.has(id));

      return { newId, oldId };
    },
    refreshAccessPoints() {
      if (!this.getUserTenants) {
        return;
      }

      const accessPointIds = [];

      for (const tenant of this.getUserTenants) {
        if (tenant.accessPoints) {
          for (let i = 0; i < tenant.accessPoints.length; i++) {
            accessPointIds.push(tenant.accessPoints[i].node.id);
          }
        }
      }

      this.accessPointIds = accessPointIds;
      this.ignoreAccessPointIdsUpdate = true;
    },
    removeFolder(nodeId) {
      this.deleteNodeId = nodeId;
      this.showingAccesspointDeleteDialog = true;
    },
    accesspointAddClosed() {
      this.$nextTick(() => {
        this.addNewNodeId = '';

        this.refreshUsers()
          .catch(error => {
            console.log(error);
          });
      });

    },
    accesspointUpdatedClosed() {
      this.accesspointUpdateOldId = '';
      this.accesspointUpdateNewId = '';

      this.refreshUsers()
        .catch(error => {
          console.log(error);
        });
    },
    accesspointDeleteClosed() {
      this.deleteNodeId = '';

      this.refreshUsers()
        .catch(error => {
          console.log(error);
        });
    },
    refreshUsers() {
      const authStore = useAuthStore();
      const accesspointId = authStore.rootNodeId;

      const payload = { accessPointId: accesspointId };
      const userStore = useUserStore();
      return userStore.refreshUsers(payload);
    },
    checkUser(userId, nodeId) {
      const payload = { userId, nodeId };

      const userStore = useUserStore();
      return userStore.pingUser(payload);
    },
    canControlNodeUsers(nodeId) {
      const nodeStore = useNodeStore();
      let node = nodeStore.getNodeById(nodeId);

      if (this.$allowedTo('control', 'User', node.id)) {
        return true;
      }

      while (node.parentid) {
        node = nodeStore.getNodeById(node.parentid);
        if (node && this.$allowedTo('control', 'User', node.id)) {
          return true;
        }
      }

      return false;
    },
    updateAccessPointId(index, value) {
        // Update the selectedAccessPointId
        this.selectedAccessPointId = value;

        // Update the array reactively
        const newArray = [...this.accessPointIds];
        newArray[index] = value;
        this.accessPointIds = newArray;
      },
  },
  computed: {
    getEditUser() {
      const userStore = useUserStore();
      return userStore.getEditUser;
    },
    getActiveTenant: function() {
      const authStore = useAuthStore();
      return authStore.getActiveTenant;
    },
    getUserTenants() {
      let userTenants = [];

      if (this.getActiveTenant && this.getEditUser && this.getEditUser.tenants) {
        userTenants = this.getEditUser.tenants.find(x => x.id === this.getActiveTenant.id);
        if (userTenants) {
          userTenants = [userTenants];
        }
      }

      return userTenants;
    },
    getDisabledNodeIds() {
      return (nodeId) => {
        let explicitlyDisabledAccessPointIds = [];

        const nodeStore = useNodeStore();
        const accessibleAccessPoints = nodeStore.getAccessPoints;

        if (accessibleAccessPoints.length > 1) {
          const accessibleAccessPointIds = accessibleAccessPoints.map((o) => o.id);
          explicitlyDisabledAccessPointIds = accessibleAccessPointIds.filter((x) => {
            return x !== this.activeAccessPointId && !this.accessPointIds.includes(x);
          });
        }

        return this.accessPointIds.filter(x => x !== nodeId).concat(explicitlyDisabledAccessPointIds);
      }
    },
    hasMoreThanOneAccessPoints() {
      return this.accessPointIds.length > 1;
    },
    activeAccessPointId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    hasLoadedAllAccesspoints() {
      const nodeStore = useNodeStore();
      for (const id of this.accessPointIds) {
        if (!nodeStore.getNodeById(id)) {
          return false;
        }
      }

      return true;
    },
  }
}
</script>

<style lang="scss" scoped>
.userAccesspoints .v-list-item > div:first-child {
  flex-grow: 1;
}

:deep(.v-theme--dark).user-permissions-toolbar {
  background-color: #272727 !important;
}

:deep(.v-theme--light).user-permissions-toolbar {
  background-color: white !important;
}

.v-toolbar :deep(.v-toolbar__content) {
  min-height: 64px !important;
  // Allow the toolbar to expand to accommodate lengthy name and e-mail especially in the cases
  // of mobile devices.
  height: unset !important;
}
</style>
